import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import customersService, { Customer } from '../../services/clientsService';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from '../../components/ui/input-otp';
import { PhoneInput } from '../../components/ui/phone-input';
import {
  Plus,
  Search,
  Edit,
  User,
  Building2,
  Receipt,
  Phone,
  Mail,
  MapPin,
  CheckCircle,
  XCircle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import LoadingScreen from '../../components/ui/LoadingScreen';
import { PERMISSIONS } from '../../constants/permissions';
import RequirePermission from '../../components/RequirePermission';
import { showSuccess, showWarning } from '../../components/Alerts';
import { AtLimit } from '../../components/ui/char-counter';
import { ClientStatusBadge } from '../../components/Clients/ClientStatusBadge';
import { handleLoadError, handleCreateError, handleUpdateError, handleApiError } from '../../utils/errorHandler';
import { formatCuit, formatPhone } from '../../utils/format';

const ITEMS_PER_PAGE = 12;

const Clients: React.FC = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [clientTypeFilter, setClientTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);

  // Dialog states
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [statusChangeDialogOpen, setStatusChangeDialogOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);

  // Selection states
  const [editingClient, setEditingClient] = useState<Customer | null>(null);
  const [clientToChangeStatus, setClientToChangeStatus] = useState<Customer | null>(null);
  const [clientToViewContact, setClientToViewContact] = useState<Customer | null>(null);

  // Sort states
  const [sortColumn, setSortColumn] = useState<'name' | 'cuit' | 'contact' | 'location' | 'createdAt'>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Field-level validation states
  const [cuitInvalid, setCuitInvalid] = useState(false);
  const [firstNameInvalid, setFirstNameInvalid] = useState(false);
  const [lastNameInvalid, setLastNameInvalid] = useState(false);
  const [businessNameInvalid, setBusinessNameInvalid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    clientType: 'PERSON' as 'PERSON' | 'COMPANY',
    cuit: '',
    firstName: '',
    lastName: '',
    businessName: '',
    companyType: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    legalAddress: '',
    address: '',
    phone: '',
    email: '',
  });

  const loadClients = useCallback(async () => {
    try {
      setLoading(true);
      const data = await customersService.getAllCustomers();
      setClients(data);
    } catch (error) {
      handleLoadError(error, 'clientes');
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const resetFieldErrors = () => {
    setCuitInvalid(false);
    setFirstNameInvalid(false);
    setLastNameInvalid(false);
    setBusinessNameInvalid(false);
  };

  const resetForm = () => {
    setFormData({
      clientType: 'PERSON',
      cuit: '',
      firstName: '',
      lastName: '',
      businessName: '',
      companyType: '',
      contactName: '',
      contactPhone: '',
      contactEmail: '',
      legalAddress: '',
      address: '',
      phone: '',
      email: '',
    });
    setEditingClient(null);
    resetFieldErrors();
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'cuit' && value) setCuitInvalid(false);
    if (field === 'firstName' && value?.trim()) setFirstNameInvalid(false);
    if (field === 'lastName' && value?.trim()) setLastNameInvalid(false);
    if (field === 'businessName' && value?.trim()) setBusinessNameInvalid(false);
  };

  const isValidCuit = (cuit: string): boolean => {
    const numbers = cuit.replace(/\D/g, '');
    return numbers.length === 11;
  };

  const handleAddNew = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const handleEdit = (client: Customer) => {
    setEditingClient(client);
    resetFieldErrors();

    if (client.type === 'PERSON') {
      setFormData({
        clientType: 'PERSON',
        cuit: client.cuit,
        firstName: client.firstName,
        lastName: client.lastName,
        businessName: '',
        companyType: '',
        contactName: '',
        contactPhone: '',
        contactEmail: '',
        legalAddress: '',
        address: client.address || '',
        phone: client.phone || '',
        email: client.email || '',
      });
    } else {
      setFormData({
        clientType: 'COMPANY',
        cuit: client.cuit,
        firstName: '',
        lastName: '',
        businessName: client.businessName,
        companyType: client.companyType || '',
        contactName: client.contactName || '',
        contactPhone: client.contactPhone || '',
        contactEmail: client.contactEmail || '',
        legalAddress: client.legalAddress || '',
        address: client.address || '',
        phone: client.phone || '',
        email: client.email || '',
      });
    }
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.cuit.trim() || !isValidCuit(formData.cuit)) {
      setCuitInvalid(true);
      showWarning('CUIT inválido', 'El CUIT debe tener 11 dígitos');
      return;
    }
    if (formData.clientType === 'PERSON') {
      if (!formData.firstName.trim()) {
        setFirstNameInvalid(true);
        showWarning('Campo obligatorio', 'El nombre es obligatorio');
        return;
      }
      if (!formData.lastName.trim()) {
        setLastNameInvalid(true);
        showWarning('Campo obligatorio', 'El apellido es obligatorio');
        return;
      }
    } else {
      if (!formData.businessName.trim()) {
        setBusinessNameInvalid(true);
        showWarning('Campo obligatorio', 'La razón social es obligatoria');
        return;
      }
    }

    setIsSubmitting(true);
    try {
      if (editingClient) {
        // Actualizar cliente existente (enviando datos completos para permitir cambio de tipo)
        if (formData.clientType === 'PERSON') {
          const personData = {
            type: 'PERSON' as const,
            cuit: formData.cuit,
            firstName: formData.firstName,
            lastName: formData.lastName,
            phone: formData.phone || undefined,
            email: formData.email || undefined,
            address: formData.address || undefined,
          };
          await customersService.updateCustomer(editingClient.id, personData);
        } else {
          const companyData = {
            type: 'COMPANY' as const,
            cuit: formData.cuit,
            businessName: formData.businessName,
            companyType: formData.companyType || undefined,
            contactName: formData.contactName || undefined,
            contactPhone: formData.contactPhone || undefined,
            contactEmail: formData.contactEmail || undefined,
            legalAddress: formData.legalAddress || undefined,
            phone: formData.phone || undefined,
            email: formData.email || undefined,
            address: formData.address || undefined,
          };
          await customersService.updateCustomer(editingClient.id, companyData);
        }
        showSuccess('Cliente actualizado exitosamente');
      } else {
        // Crear nuevo cliente
        if (formData.clientType === 'PERSON') {
          const personData = {
            type: 'PERSON' as const,
            cuit: formData.cuit,
            firstName: formData.firstName,
            lastName: formData.lastName,
            phone: formData.phone || undefined,
            email: formData.email || undefined,
            address: formData.address || undefined,
          };
          await customersService.createCustomer(personData);
        } else {
          const companyData = {
            type: 'COMPANY' as const,
            cuit: formData.cuit,
            businessName: formData.businessName,
            companyType: formData.companyType || undefined,
            contactName: formData.contactName || undefined,
            contactPhone: formData.contactPhone || undefined,
            contactEmail: formData.contactEmail || undefined,
            legalAddress: formData.legalAddress || undefined,
            phone: formData.phone || undefined,
            email: formData.email || undefined,
            address: formData.address || undefined,
          };
          await customersService.createCustomer(companyData);
        }
        showSuccess('Cliente creado exitosamente');
      }

      setIsDialogOpen(false);
      resetForm();
      loadClients();
    } catch (error: any) {
      if (editingClient) {
        handleUpdateError(error, 'cliente');
      } else {
        handleCreateError(error, 'cliente');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStatusChange = (client: Customer) => {
    setClientToChangeStatus(client);
    setStatusChangeDialogOpen(true);
  };

  const confirmStatusChange = async () => {
    if (!clientToChangeStatus) return;
    const newStatus = !clientToChangeStatus.isActive;
    try {
      await customersService.changeCustomerStatus(clientToChangeStatus.id, newStatus);
      showSuccess(`Cliente ${newStatus ? 'activado' : 'desactivado'} exitosamente`);
      loadClients();
    } catch (error) {
      handleApiError(error, `Error al ${newStatus ? 'activar' : 'desactivar'} el cliente`);
    } finally {
      setStatusChangeDialogOpen(false);
      setClientToChangeStatus(null);
    }
  };

  const getClientName = (client: Customer) => {
    return client.type === 'PERSON'
      ? `${client.firstName} ${client.lastName}`
      : client.businessName;
  };

  const getSearchableText = (client: Customer): string => {
    const baseText = `${client.cuit} ${client.email || ''} ${client.address || ''}`;
    if (client.type === 'PERSON') {
      return `${baseText} ${client.firstName} ${client.lastName}`.toLowerCase();
    } else {
      return `${baseText} ${client.businessName} ${client.companyType || ''}`.toLowerCase();
    }
  };

  const applyFilters = () => {
    return clients.filter(client => {
      const matchesSearch = getSearchableText(client).includes(searchTerm.toLowerCase());
      const matchesType = clientTypeFilter === 'all' ||
        (clientTypeFilter === 'PERSON' && client.type === 'PERSON') ||
        (clientTypeFilter === 'COMPANY' && client.type === 'COMPANY');
      const matchesStatus = statusFilter === 'all' ||
        (statusFilter === 'active' && client.isActive) ||
        (statusFilter === 'inactive' && !client.isActive);

      return matchesSearch && matchesType && matchesStatus;
    });
  };

  const sortClients = (filteredClients: Customer[]) => {
    return filteredClients.sort((a, b) => {
      let comparison = 0;

      switch (sortColumn) {
        case 'name':
          comparison = getClientName(a).localeCompare(getClientName(b));
          break;
        case 'cuit':
          comparison = a.cuit.localeCompare(b.cuit);
          break;
        case 'contact':
          comparison = (a.email || '').localeCompare(b.email || '');
          break;
        case 'location':
          comparison = (a.address || '').localeCompare(b.address || '');
          break;
        case 'createdAt':
          comparison = new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();
          break;
        default:
          comparison = 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _handleSort = (column: 'name' | 'cuit' | 'contact' | 'location' | 'createdAt') => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const filteredAndSortedClients = sortClients(applyFilters());
  const totalPages = Math.ceil(filteredAndSortedClients.length / ITEMS_PER_PAGE);
  const safePage = Math.min(page, Math.max(1, totalPages));
  const pageClients = filteredAndSortedClients.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); }, [searchTerm, clientTypeFilter, statusFilter]);

  if (loading) {
    return <LoadingScreen message="Cargando clientes..." />;
  }

  return (
    <div className="w-full h-full p-6 space-y-6">
      {/* Card Container */}
      <div className="overflow-hidden border rounded-xl shadow-sm bg-card border-border">
        {/* Header */}
        <div className="px-6 py-5 border-b border-border bg-gradient-to-r from-card to-muted/30">
          <div className="flex flex-col items-start justify-between gap-4 mb-6 sm:flex-row sm:items-center">
            <div className="flex items-center gap-4">
              <div className="p-2.5 rounded-xl bg-primary/10">
                <User className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Lista de Clientes</h1>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  Gestiona y visualiza información de todos los clientes
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                <Building2 className="w-3.5 h-3.5" />
                {clients.length} cliente{clients.length !== 1 ? 's' : ''}
              </span>
              <RequirePermission permission={PERMISSIONS.COMERCIAL.CLIENTES_EDITAR}>
                <Button
                  size="sm"
                  onClick={handleAddNew}
                  className="flex items-center gap-2 rounded-lg bg-primary text-primary-foreground hover:opacity-90 transition-opacity duration-200"
                >
                  <Plus className="w-4 h-4" />
                  Nuevo Cliente
                </Button>
              </RequirePermission>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col items-end gap-4 md:flex-row">
            {/* Filters */}
            <div className="flex flex-wrap gap-4">
              <div className="space-y-2">
                <label className="text-xs font-medium uppercase text-muted-foreground">
                  Tipo de Cliente
                </label>
                <Select value={clientTypeFilter} onValueChange={setClientTypeFilter}>
                  <SelectTrigger className="w-[180px] rounded-lg">
                    <SelectValue placeholder="Todos los tipos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los tipos</SelectItem>
                    <SelectItem value="PERSON">Persona Física</SelectItem>
                    <SelectItem value="COMPANY">Empresa</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-medium uppercase text-muted-foreground">
                  Estado
                </label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[180px] rounded-lg">
                    <SelectValue placeholder="Todos los estados" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los estados</SelectItem>
                    <SelectItem value="active">Activos</SelectItem>
                    <SelectItem value="inactive">Inactivos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Search */}
            <div className="w-full space-y-2 md:w-72 md:ml-auto">
              <label className="text-xs font-medium uppercase text-muted-foreground">
                Buscar
              </label>
              <div className="relative">
                <Search className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2 text-muted-foreground" />
                <Input
                  placeholder="Buscar clientes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="rounded-lg pl-9 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Cards Grid */}
        <div className="p-6">
          {filteredAndSortedClients.length === 0 ? (
            <div className="py-16 text-center">
              <div className="mx-auto mb-5 w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                <User className="w-8 h-8 text-primary" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-foreground">No hay clientes</h3>
              <p className="mb-6 text-sm text-muted-foreground max-w-xs mx-auto">Comienza registrando el primer cliente para gestionar su información y cuenta corriente.</p>
              <RequirePermission permission={PERMISSIONS.COMERCIAL.CLIENTES_EDITAR}>
                <Button
                  onClick={handleAddNew}
                  className="bg-primary text-primary-foreground hover:opacity-90 transition-opacity duration-200"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo Cliente
                </Button>
              </RequirePermission>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {pageClients.map((client) => {
                  const isCompany = client.type === 'COMPANY';
                  const iconBg = isCompany
                    ? 'bg-primary/10 text-primary'
                    : 'bg-accent text-accent-foreground';

                  return (
                    <div
                      key={client.id}
                      className="flex flex-col bg-card border border-border rounded-xl overflow-hidden transition-shadow duration-200 hover:shadow-md"
                    >
                      {/* Card Header */}
                      <div className="flex items-start justify-between px-4 pt-4 pb-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`flex-shrink-0 p-2.5 rounded-xl ${iconBg}`}>
                            {isCompany
                              ? <Building2 className="w-5 h-5" />
                              : <User className="w-5 h-5" />
                            }
                          </div>
                          <div className="min-w-0">
                            <p
                              className="text-sm font-semibold text-foreground truncate leading-tight"
                              title={getClientName(client)}
                            >
                              {getClientName(client)}
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {isCompany
                                ? (client.companyType ? `Empresa · ${client.companyType}` : 'Empresa')
                                : 'Persona Física'
                              }
                            </p>
                          </div>
                        </div>
                        <div className="flex-shrink-0 ml-2">
                          <ClientStatusBadge isActive={client.isActive} size="sm" />
                        </div>
                      </div>

                      {/* Divider */}
                      <div className="mx-4 border-t border-border" />

                      {/* Info rows */}
                      <div className="px-4 py-3 space-y-2">
                        {/* CUIT */}
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-muted-foreground w-10 flex-shrink-0">CUIT</span>
                          <span className="font-mono text-xs font-semibold text-foreground tracking-wide">
                            {formatCuit(client.cuit)}
                          </span>
                        </div>

                        {/* Email o teléfono */}
                        {(client.email || client.phone) && (
                          <div className="flex items-center gap-2">
                            {client.email ? (
                              <>
                                <Mail className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                                <span className="text-xs text-foreground truncate">{client.email}</span>
                              </>
                            ) : (
                              <>
                                <Phone className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                                <span className="text-xs text-foreground">{formatPhone(client.phone!)}</span>
                              </>
                            )}
                          </div>
                        )}

                        {/* Dirección */}
                        {client.address && (
                          <div className="flex items-start gap-2">
                            <MapPin className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0 mt-0.5" />
                            <span className="text-xs text-foreground line-clamp-1">{client.address}</span>
                          </div>
                        )}
                      </div>

                      {/* Contact block (solo empresas con contacto) */}
                      {isCompany && (client.contactName || client.contactPhone || client.contactEmail) && (
                        <>
                          <div className="mx-4 border-t border-border" />
                          <div className="px-4 py-2.5 bg-muted/50">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1.5">Contacto</p>
                            <div className="space-y-1">
                              {client.contactName && (
                                <div className="flex items-center gap-2">
                                  <User className="w-3 h-3 text-primary/60 flex-shrink-0" />
                                  <span className="text-xs text-foreground">{client.contactName}</span>
                                </div>
                              )}
                              {client.contactPhone && (
                                <div className="flex items-center gap-2">
                                  <Phone className="w-3 h-3 text-primary/60 flex-shrink-0" />
                                  <span className="text-xs text-foreground">{client.contactPhone}</span>
                                </div>
                              )}
                              {client.contactEmail && (
                                <div className="flex items-center gap-2">
                                  <Mail className="w-3 h-3 text-primary/60 flex-shrink-0" />
                                  <span className="text-xs text-foreground truncate">{client.contactEmail}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </>
                      )}

                      {/* Footer: fecha + acciones */}
                      <div className="mt-auto mx-4 border-t border-border" />
                      <div className="flex items-center justify-between px-4 py-2.5">
                        <span className="text-xs text-muted-foreground font-mono">
                          {client.createdAt
                            ? new Date(client.createdAt).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' })
                            : '—'
                          }
                        </span>
                        <div className="flex items-center gap-0.5">
                          <RequirePermission permission={PERMISSIONS.CONTABILIDAD.CUENTAS_CORRIENTES_VER}>
                            <button
                              onClick={() => navigate(`/clients/${client.id}/current-account`)}
                              className="inline-flex items-center justify-center p-1.5 rounded-lg transition-colors duration-200 hover:bg-primary/10 text-muted-foreground hover:text-primary"
                              title="Cuenta Corriente"
                            >
                              <Receipt className="w-4 h-4" />
                            </button>
                          </RequirePermission>
                          {isCompany && (
                            <button
                              onClick={() => { setClientToViewContact(client); setContactDialogOpen(true); }}
                              className="inline-flex items-center justify-center p-1.5 rounded-lg transition-colors duration-200 hover:bg-primary/10 text-muted-foreground hover:text-primary"
                              title="Ver Contacto"
                            >
                              <Phone className="w-4 h-4" />
                            </button>
                          )}
                          <RequirePermission permission={PERMISSIONS.COMERCIAL.CLIENTES_EDITAR}>
                            <button
                              onClick={() => handleEdit(client)}
                              className="inline-flex items-center justify-center p-1.5 rounded-lg transition-colors duration-200 hover:bg-accent text-muted-foreground hover:text-foreground"
                              title="Editar cliente"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleStatusChange(client)}
                              className={`inline-flex items-center justify-center p-1.5 rounded-lg transition-colors duration-200 ${
                                client.isActive
                                  ? 'text-destructive hover:bg-destructive/10 hover:text-destructive'
                                  : 'text-success hover:bg-success/10 hover:text-success'
                              }`}
                              title={client.isActive ? 'Desactivar cliente' : 'Activar cliente'}
                            >
                              {client.isActive
                                ? <XCircle className="w-4 h-4" />
                                : <CheckCircle className="w-4 h-4" />
                              }
                            </button>
                          </RequirePermission>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between pt-4 mt-2 border-t border-border">
                <p className="text-sm text-muted-foreground">
                  Mostrando <span className="font-semibold text-foreground">{pageClients.length}</span> de <span className="font-semibold text-foreground">{filteredAndSortedClients.length}</span> cliente{filteredAndSortedClients.length !== 1 ? 's' : ''}
                </p>
                {totalPages > 1 && (
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={safePage === 1}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-foreground">{safePage} / {totalPages}</span>
                    <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={safePage === totalPages}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => {
        setIsDialogOpen(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="max-w-2xl flex flex-col gap-0 p-0 max-h-[92vh]">
          {/* Header fijo */}
          <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-border">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3 text-xl font-bold text-foreground">
                {editingClient
                  ? <Edit className="w-5 h-5 text-primary" />
                  : <Plus className="w-5 h-5 text-primary" />
                }
                {editingClient ? 'Editar Cliente' : 'Nuevo Cliente'}
              </DialogTitle>
            </DialogHeader>
          </div>

          {/* Cuerpo scrolleable */}
          <div className="flex-1 overflow-y-auto px-6 py-5 min-h-0">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-medium text-foreground sm:text-sm">Tipo de Cliente</Label>
                <Select
                  value={formData.clientType}
                  onValueChange={(value) => handleInputChange('clientType', value as 'PERSON' | 'COMPANY')}
                >
                  <SelectTrigger className="rounded-lg bg-background border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PERSON">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        <span>Persona Física</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="COMPANY">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        <span>Empresa</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.clientType === 'PERSON' ? (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="firstName" className="text-xs font-medium text-foreground sm:text-sm">
                      Nombre <span className="text-xs text-red-500">(Campo obligatorio)</span>
                    </Label>
                    <Input
                      id="firstName"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      placeholder="Nombre"
                      maxLength={255}
                      className={`rounded-lg bg-background border-border focus-visible:ring-primary ${firstNameInvalid ? 'border-red-500 border-2 focus-visible:ring-red-500' : ''}`}
                    />
                    <AtLimit value={formData.firstName} max={255} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName" className="text-xs font-medium text-foreground sm:text-sm">
                      Apellido <span className="text-xs text-red-500">(Campo obligatorio)</span>
                    </Label>
                    <Input
                      id="lastName"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      placeholder="Apellido"
                      maxLength={255}
                      className={`rounded-lg bg-background border-border focus-visible:ring-primary ${lastNameInvalid ? 'border-red-500 border-2 focus-visible:ring-red-500' : ''}`}
                    />
                    <AtLimit value={formData.lastName} max={255} />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="cuit" className="text-xs font-medium text-foreground sm:text-sm">
                      CUIT <span className="text-xs text-red-500">(Campo obligatorio)</span>
                    </Label>
                    <InputOTP
                      maxLength={11}
                      pushPasswordManagerStrategy="none"
                      value={formData.cuit.replace(/\D/g, '')}
                      onChange={(value) => {
                        let formatted = value;
                        if (value.length > 2 && value.length <= 10) {
                          formatted = `${value.slice(0, 2)}-${value.slice(2)}`;
                        } else if (value.length > 10) {
                          formatted = `${value.slice(0, 2)}-${value.slice(2, 10)}-${value.slice(10)}`;
                        }
                        handleInputChange('cuit', formatted);
                      }}
                    >
                      <InputOTPGroup className={cuitInvalid ? 'ring-2 ring-red-500 rounded-l-md' : ''}>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                      </InputOTPGroup>
                      <InputOTPSeparator />
                      <InputOTPGroup className={cuitInvalid ? 'ring-2 ring-red-500' : ''}>
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                        <InputOTPSlot index={6} />
                        <InputOTPSlot index={7} />
                        <InputOTPSlot index={8} />
                        <InputOTPSlot index={9} />
                      </InputOTPGroup>
                      <InputOTPSeparator />
                      <InputOTPGroup className={cuitInvalid ? 'ring-2 ring-red-500 rounded-r-md' : ''}>
                        <InputOTPSlot index={10} />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-xs font-medium text-foreground sm:text-sm">Teléfono</Label>
                    <PhoneInput
                      value={formData.phone}
                      onChange={(value) => handleInputChange('phone', value)}
                      placeholder="Telefono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-xs font-medium text-foreground sm:text-sm">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="ejemplo@email.com"
                      maxLength={255}
                      className="rounded-lg bg-background border-border focus-visible:ring-primary"
                    />
                    <AtLimit value={formData.email} max={255} />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="address" className="text-xs font-medium text-foreground sm:text-sm">Dirección</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      placeholder="Calle 123, Ciudad"
                      maxLength={255}
                      className="rounded-lg bg-background border-border focus-visible:ring-primary"
                    />
                    <AtLimit value={formData.address} max={255} />
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="businessName" className="text-xs font-medium text-foreground sm:text-sm">
                        Razón Social <span className="text-xs text-red-500">(Campo obligatorio)</span>
                      </Label>
                      <Input
                        id="businessName"
                        value={formData.businessName}
                        onChange={(e) => handleInputChange('businessName', e.target.value)}
                        placeholder="Empresa S.A."
                        maxLength={255}
                        className={`rounded-lg bg-background border-border focus-visible:ring-primary ${businessNameInvalid ? 'border-red-500 border-2 focus-visible:ring-red-500' : ''}`}
                      />
                      <AtLimit value={formData.businessName} max={255} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="companyType" className="text-xs font-medium text-foreground sm:text-sm">Tipo de Sociedad</Label>
                      <Input
                        id="companyType"
                        value={formData.companyType}
                        onChange={(e) => handleInputChange('companyType', e.target.value)}
                        placeholder="S.A., S.R.L."
                        maxLength={255}
                        className="rounded-lg bg-background border-border focus-visible:ring-primary"
                      />
                      <AtLimit value={formData.companyType} max={255} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cuit" className="text-xs font-medium text-foreground sm:text-sm">
                      CUIT <span className="text-xs text-red-500">(Campo obligatorio)</span>
                    </Label>
                    <InputOTP
                      maxLength={11}
                      pushPasswordManagerStrategy="none"
                      value={formData.cuit.replace(/\D/g, '')}
                      onChange={(value) => {
                        let formatted = value;
                        if (value.length > 2 && value.length <= 10) {
                          formatted = `${value.slice(0, 2)}-${value.slice(2)}`;
                        } else if (value.length > 10) {
                          formatted = `${value.slice(0, 2)}-${value.slice(2, 10)}-${value.slice(10)}`;
                        }
                        handleInputChange('cuit', formatted);
                      }}
                    >
                      <InputOTPGroup className={cuitInvalid ? 'ring-2 ring-red-500 rounded-l-md' : ''}>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                      </InputOTPGroup>
                      <InputOTPSeparator />
                      <InputOTPGroup className={cuitInvalid ? 'ring-2 ring-red-500' : ''}>
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                        <InputOTPSlot index={6} />
                        <InputOTPSlot index={7} />
                        <InputOTPSlot index={8} />
                        <InputOTPSlot index={9} />
                      </InputOTPGroup>
                      <InputOTPSeparator />
                      <InputOTPGroup className={cuitInvalid ? 'ring-2 ring-red-500 rounded-r-md' : ''}>
                        <InputOTPSlot index={10} />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-xs font-medium text-foreground sm:text-sm">Teléfono</Label>
                      <PhoneInput
                        value={formData.phone}
                        onChange={(value) => handleInputChange('phone', value)}
                        placeholder="3564 335598"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-xs font-medium text-foreground sm:text-sm">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="empresa@email.com"
                        maxLength={255}
                        className="rounded-lg bg-background border-border focus-visible:ring-primary"
                      />
                      <AtLimit value={formData.email} max={255} />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="address" className="text-xs font-medium text-foreground sm:text-sm">Dirección</Label>
                      <Input
                        id="address"
                        value={formData.address}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        placeholder="Calle 123, Ciudad"
                        maxLength={255}
                        className="rounded-lg bg-background border-border focus-visible:ring-primary"
                      />
                      <AtLimit value={formData.address} max={255} />
                    </div>
                  </div>

                  <div className="pt-3 border-t border-border">
                    <h4 className="mb-3 text-sm font-semibold text-foreground">Persona de Contacto</h4>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="contactName" className="text-xs font-medium text-foreground sm:text-sm">Nombre</Label>
                        <Input
                          id="contactName"
                          value={formData.contactName}
                          onChange={(e) => handleInputChange('contactName', e.target.value)}
                          placeholder="Juan Pérez"
                          maxLength={255}
                          className="rounded-lg bg-background border-border focus-visible:ring-primary"
                        />
                        <AtLimit value={formData.contactName} max={255} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contactPhone" className="text-xs font-medium text-foreground sm:text-sm">Teléfono Contacto</Label>
                        <PhoneInput
                          value={formData.contactPhone}
                          onChange={(value) => handleInputChange('contactPhone', value)}
                          placeholder="3564 335598"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contactEmail" className="text-xs font-medium text-foreground sm:text-sm">Email Contacto</Label>
                        <Input
                          id="contactEmail"
                          type="email"
                          value={formData.contactEmail}
                          onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                          placeholder="contacto@email.com"
                          maxLength={255}
                          className="rounded-lg bg-background border-border focus-visible:ring-primary"
                        />
                        <AtLimit value={formData.contactEmail} max={255} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="legalAddress" className="text-xs font-medium text-foreground sm:text-sm">Domicilio Legal</Label>
                        <Input
                          id="legalAddress"
                          value={formData.legalAddress}
                          onChange={(e) => handleInputChange('legalAddress', e.target.value)}
                          placeholder="Dirección fiscal"
                          maxLength={255}
                          className="rounded-lg bg-background border-border focus-visible:ring-primary"
                        />
                        <AtLimit value={formData.legalAddress} max={255} />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer fijo */}
          <div className="flex-shrink-0 flex justify-end gap-3 px-6 py-4 border-t border-border">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)} className="border-input hover:bg-accent text-foreground">
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={isSubmitting} className="bg-primary text-primary-foreground hover:opacity-90">
              {isSubmitting ? 'Guardando...' : (editingClient ? 'Actualizar' : 'Crear') + ' Cliente'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Status Change Dialog */}
      <Dialog open={statusChangeDialogOpen} onOpenChange={setStatusChangeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {clientToChangeStatus?.isActive ? (
                <XCircle className="w-5 h-5 text-destructive" />
              ) : (
                <CheckCircle className="w-5 h-5 text-success" />
              )}
              {clientToChangeStatus?.isActive ? 'Desactivar Cliente' : 'Activar Cliente'}
            </DialogTitle>
            <DialogDescription>
              ¿Está seguro de que desea {clientToChangeStatus?.isActive ? 'desactivar' : 'activar'} a{' '}
              <span className="font-medium text-foreground">
                {clientToChangeStatus ? getClientName(clientToChangeStatus) : ''}
              </span>?
              {clientToChangeStatus?.isActive && (
                <span className="block mt-2 text-sm">
                  El cliente no aparecerá en las búsquedas activas pero podrá reactivarlo cuando lo necesite.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => setStatusChangeDialogOpen(false)}>
              Cancelar
            </Button>
            {clientToChangeStatus?.isActive ? (
              <Button variant="destructive" onClick={confirmStatusChange}>
                <XCircle className="w-4 h-4 mr-2" />
                Desactivar
              </Button>
            ) : (
              <Button className="bg-success text-success-foreground hover:bg-success/90" onClick={confirmStatusChange}>
                <CheckCircle className="w-4 h-4 mr-2" />
                Activar
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Contact Dialog */}
      <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              Información de Contacto
            </DialogTitle>
            <DialogDescription>
              Datos del contacto de la empresa
            </DialogDescription>
          </DialogHeader>

          {clientToViewContact && clientToViewContact.type === 'COMPANY' && (
            <div className="pt-4 space-y-4">
              {clientToViewContact.contactName && (
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs font-medium uppercase text-muted-foreground">Nombre</p>
                    <p className="text-sm text-foreground">{clientToViewContact.contactName}</p>
                  </div>
                </div>
              )}

              {clientToViewContact.contactPhone && (
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs font-medium uppercase text-muted-foreground">Teléfono</p>
                    <p className="text-sm text-foreground">{clientToViewContact.contactPhone}</p>
                  </div>
                </div>
              )}

              {clientToViewContact.contactEmail && (
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs font-medium uppercase text-muted-foreground">Email</p>
                    <p className="text-sm text-foreground">{clientToViewContact.contactEmail}</p>
                  </div>
                </div>
              )}

              {clientToViewContact.legalAddress && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs font-medium uppercase text-muted-foreground">Domicilio Legal</p>
                    <p className="text-sm text-foreground">{clientToViewContact.legalAddress}</p>
                  </div>
                </div>
              )}

              {!clientToViewContact.contactName &&
                !clientToViewContact.contactPhone &&
                !clientToViewContact.contactEmail &&
                !clientToViewContact.legalAddress && (
                  <div className="py-6 text-center">
                    <p className="text-sm text-muted-foreground">No hay información de contacto disponible</p>
                  </div>
                )}
            </div>
          )}

          <div className="flex justify-end pt-4 border-t">
            <Button onClick={() => setContactDialogOpen(false)}>
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Clients;